import cx from "classnames";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useState,
  CSSProperties,
} from "react";
import styles from "./Toaster.module.scss";
import { useToasts } from "./ToastProvider";
import { Button } from "../Button";
import { toast } from "./toast-methods";
import { Toast, ToastProps } from "./Toast";
import {
  ANIMATION_DURATION,
  useShowMoreButtonPositionStyles,
} from "./toast-utils";
import { SrOnly } from "../SrOnly";
import { useMergeRefs } from "../../hooks";
import useHotkeyFocus from "./internal/useHotkeyFocus";

/**
 * Props for the ToastStack component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ToastStackProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> & {
  /**
   * Duration of the toast in milliseconds. If set to `false`, the toast will not auto-dismiss.
   * If `actions` are provided in the toast method, this prop will be ignored and treated as `false`.
   * @default false
   */
  duration?: number | false;
  /**
   * How a screen-reader will announce the hotkey used to set focus on the first toast.
   * @default "t"
   */
  focusKey?: string;
  /**
   * How many toasts to display before they start stacking.
   * @default 3
   */
  toastsBeforeStack?: number;
  /**
   * The toasts to display in the stack.
   * @default []
   */
  toasts: ToastProps[];
};

/**
 * ToastStack component for displaying a stack of toast notifications.
 *
 * Features:
 * - Displays multiple toasts in a vertical stack
 * - Automatic stacking behavior when toast count exceeds limit
 * - Show more button to expand stacked toasts
 * - Keyboard navigation with hotkey support
 * - Hover pause functionality for toast timers
 * - Smooth animations and transitions
 * - Accessibility features with screen reader support
 * - Responsive design with mobile touch support
 * - Focus management for keyboard users
 *
 * @example
 * <ToastStack
 *   toasts={toastList}
 *   duration={5000}
 *   toastsBeforeStack={3}
 *   focusKey="t"
 * />
 *
 * @example
 * <ToastStack
 *   toasts={[toast1, toast2]}
 *   duration={false}
 *   toastsBeforeStack={5}
 * />
 */
export const ToastStack = forwardRef<HTMLDivElement, ToastStackProps>(
  (
    {
      className,
      duration = false,
      focusKey = "t",
      toasts,
      toastsBeforeStack = 3,
      ...props
    },
    ref,
  ) => {
    const toastStackRef = useRef<HTMLDivElement>(null);
    const firstToastRef = useRef<HTMLDivElement | null>(null);
    const mergedToastStackRef = useMergeRefs([toastStackRef, ref]);
    const {
      setToastsBeforeStack,
      extraToastsVisible,
      setExtraToastsVisible,
      getToastStackHeight,
      isDialogToaster,
    } = useToasts();
    const toastCount = toasts.length;
    const isToastStackEmpty = toastCount === 0;

    const lastToastCount = useRef<number>(0);

    const [toastStackHeight, setToastStackHeight] = useState<number>(0);
    const [disableAnimations, setDisableAnimations] = useState(false);
    const [toastPausedStatus, setToastPausedStatus] =
      useState<ToastProps["pausedStatus"]>(undefined);

    useEffect(() => {
      if (toastCount === 0) {
        setToastPausedStatus(undefined);
      }
    }, [toastCount]);

    useEffect(() => {
      const scrollListener = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toastStackRef.current?.scrollTo({
          top: toastStackRef.current.scrollTop + e.deltaY,
        });
      };

      if (isDialogToaster) {
        // prevent existing toasts from sliding in again
        setDisableAnimations(isDialogToaster);
        setTimeout(() => {
          setDisableAnimations(false);
        }, 500);

        // fix scrolling for toast stacks in dialogs
        const toastStackRefCurrent = toastStackRef.current;
        toastStackRefCurrent?.addEventListener("wheel", scrollListener, {
          passive: false,
        });

        return () => {
          toastStackRefCurrent?.removeEventListener("wheel", scrollListener);
        };
      }
    }, [isDialogToaster]);

    // reset toast stack height when toasts change
    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (toastCount > lastToastCount.current || isDialogToaster) {
        setToastStackHeight(getToastStackHeight());
      } else {
        timer = setTimeout(
          () => setToastStackHeight(getToastStackHeight()),
          ANIMATION_DURATION - 10,
        );
      }
      lastToastCount.current = toastCount;
      return () => {
        clearTimeout(timer);
      };
      // only run after toastStackHeight updates or dialog state changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToastStackHeight, isDialogToaster]);

    useEffect(() => {
      setToastsBeforeStack(toastsBeforeStack);
    }, [setToastsBeforeStack, toastsBeforeStack]);

    const handleToastStackBlur = useCallback(() => {
      setExtraToastsVisible(false);

      setToastPausedStatus("resumed");

      if (toastStackRef.current?.scrollTo) {
        toastStackRef.current?.scrollTo(0, 0);
      }
    }, [setExtraToastsVisible]);

    const { returnFocusRef } = useHotkeyFocus({
      disabled: isToastStackEmpty,
      hotkey: focusKey,
      onHotkeyIn: async () => {
        setExtraToastsVisible(true);
        return new Promise((resolve) => {
          // add a delay for the ToastStack to render the hidden toasts
          setTimeout(() => {
            resolve();
          }, 100);
        });
      },
      onHotkeyOut: () => {
        setExtraToastsVisible(false);
        handleToastStackBlur();
      },
      refToFocus: firstToastRef as React.MutableRefObject<HTMLElement>,
      rootRef: toastStackRef as React.MutableRefObject<HTMLElement>,
    });

    const showMoreButtonPositionStyles = useShowMoreButtonPositionStyles();

    const showMoreButton =
      !extraToastsVisible && toastCount > toastsBeforeStack;
    const extraToastsLength = toastCount - toastsBeforeStack;
    const showMoreButtonText = `${extraToastsLength} more message${extraToastsLength > 1 ? "s" : ""}`;

    const toastStackClasses = cx(styles["toast-stack"], className, {
      [styles["show-more-button"]]: showMoreButton,
      [styles["show-extra-toast"]]: extraToastsVisible,
      [styles["disable-animations"]]: disableAnimations,
    });

    const handleToastStackFocusHover = () => {
      if (toastPausedStatus === "resumed" || toastPausedStatus === undefined) {
        setToastPausedStatus(Date.now());
      }
    };

    const handleToastStackOnBlur = () => {
      if (!toastStackRef.current?.matches(":focus-within")) {
        handleToastStackBlur();
      }
    };

    const handleShowMoreButtonClick = () => {
      setExtraToastsVisible(true);
    };

    let toastStackHeightStyle: CSSProperties["height"] = 0;

    if (extraToastsVisible) {
      // need to reduce padding here due to box-sizing: content-box
      toastStackHeightStyle = "calc(100vh - var(--toast-stack-padding) * 2)";
    } else if (toastCount > toastsBeforeStack) {
      // 3rem to leave room for show more button
      toastStackHeightStyle = `calc(${toastStackHeight}px + 3rem + var(--toast-stack-padding))`;
    } else {
      toastStackHeightStyle = toastStackHeight;
    }

    return (
      <>
        <div
          data-anv="ToastStack"
          className={toastStackClasses}
          ref={mergedToastStackRef}
          onMouseOver={handleToastStackFocusHover}
          onMouseLeave={handleToastStackBlur}
          onFocus={handleToastStackFocusHover}
          onBlur={handleToastStackOnBlur}
          style={{
            height: toastStackHeightStyle,
          }}
          {...props}
        >
          {toasts.map(({ toastClassName, onClose, ...toastProps }, index) => {
            const isStacked =
              !extraToastsVisible && index < toastCount - toastsBeforeStack;

            const isLastToastDisplayed =
              index === toastCount - toastsBeforeStack;

            const isHidden = index < toastCount - toastsBeforeStack - 2;

            const toastClasses = cx(styles.toast, toastClassName);
            const toastContainerClasses = cx(styles["toast-wrapper"], {
              [styles.stacked]: isStacked || isLastToastDisplayed,
              [styles["stack-one"]]:
                isStacked && index === toastCount - toastsBeforeStack - 1,
              [styles["stack-two"]]:
                isStacked && index === toastCount - toastsBeforeStack - 2,
              [styles["stacked-extra"]]: isHidden,
              [styles["stacked-show-extra"]]: extraToastsVisible,
            });

            const handleOnClose = (
              e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
            ) => {
              onClose?.(e);
              returnFocusRef.current?.focus();
              toast.dismiss(toastProps.id);
            };

            return (
              <Toast
                containerClassName={toastContainerClasses}
                duration={duration}
                index={index}
                isStacked={isStacked}
                isBelowStackIndex={index < toastCount - toastsBeforeStack}
                key={toastProps.id}
                onClose={handleOnClose}
                pausedStatus={toastPausedStatus}
                toastClassName={toastClasses}
                ref={index === 0 ? firstToastRef : undefined}
                {...toastProps}
              />
            );
          })}
          {showMoreButton && (
            <div
              className={styles["show-more-button-container"]}
              style={showMoreButtonPositionStyles}
            >
              <Button
                className={styles["show-collapse-button"]}
                onClick={handleShowMoreButtonClick}
                appearance="secondary"
                tabIndex={0}
              >
                {showMoreButtonText}
              </Button>
            </div>
          )}
          <SrOnly aria-live="polite">
            New toast message appeared. Press the {focusKey} key to focus on the
            toast messages. Press escape to return focus to previous position.
          </SrOnly>
        </div>
        {extraToastsVisible && (
          <div className={styles["show-collapse-button-container"]}>
            <Button
              className={styles["show-collapse-button"]}
              onClick={handleToastStackBlur}
              appearance="secondary"
            >
              Collapse Messages
            </Button>
          </div>
        )}
      </>
    );
  },
);

ToastStack.displayName = "ToastStack";
